import { Rule } from 'rc-field-form/es/interface';
import { fieldErrorMessages, validationErrorMessages } from 'consts/errors/forms';

// Field name to error message mapping
const fieldNameToErrorKey: Record<string, keyof typeof fieldErrorMessages> = {
    // Personal data
    firstName: 'firstName',
    lastName: 'lastName',
    pesel: 'pesel',
    birthDate: 'birthDate',
    birthPlace: 'birthPlace',
    gender: 'gender',

    // Contact
    email: 'email',
    phoneNumber: 'phoneNumber',

    // Institution
    institutionName: 'institutionName',
    nip: 'nip',
    regon: 'regon',
    rspo: 'rspo',

    // Address
    city: 'city',
    streetAddress: 'streetAddress',
    zipCode: 'zipCode',

    // Documents
    idDocumentNumber: 'idDocumentNumber',

    // Educational
    group: 'group',
    subject: 'subject',
    teacher: 'teacher',
    supervisor: 'supervisor',

    // Dates and times
    date: 'date',
    timeFrom: 'timeFrom',
    timeTo: 'timeTo',
    dateFrom: 'dateFrom',
    dateTo: 'dateTo',

    // Content
    name: 'name',
    description: 'description',
    note: 'note',
    topic: 'topic',
    lessonNote: 'lessonNote',

    // Selections
    userRole: 'userRole',
    eventType: 'eventType',
    helpForm: 'helpForm',
    psychologicalHelpBasis: 'psychologicalHelpBasis',
    inspectionSubject: 'inspectionSubject',
    additionalInformation: 'additionalInformation',

    // Financial
    amount: 'amount',
    grossAmount: 'grossAmount',
    bankAccountNumber: 'bankAccountNumber',
};

/**
 * Gets a field-specific required error message
 * @param fieldName - The name of the field
 * @returns Specific error message for the field or generic message
 */
export const getRequiredErrorMessage = (fieldName: string): string => {
    const errorKey = fieldNameToErrorKey[fieldName];
    return errorKey ? fieldErrorMessages[errorKey] : fieldErrorMessages.required;
};

/**
 * Creates a required validation rule with field-specific error message
 * @param fieldName - The name of the field
 * @returns Validation rule object
 */
export const createRequiredRule = (fieldName: string): Rule => ({
    required: true,
    message: getRequiredErrorMessage(fieldName),
});

/**
 * Creates a min length validation rule with specific error message
 * @param min - Minimum length
 * @returns Validation rule object
 */
export const createMinLengthRule = (min: number): Rule => ({
    type: 'string',
    min,
    message: validationErrorMessages.minLength(min),
});

/**
 * Creates a max length validation rule with specific error message
 * @param max - Maximum length
 * @returns Validation rule object
 */
export const createMaxLengthRule = (max: number): Rule => ({
    type: 'string',
    max,
    message: validationErrorMessages.maxLength(max),
});
export const createMaxLengthRuleOptional = (max: number): Rule => ({
    type: 'string',
    max,
    required: false,
    message: validationErrorMessages.maxLength(max),
});

/**
 * Creates an email validation rule
 * @returns Validation rule object
 */
export const createEmailRule = (): Rule => ({
    type: 'email',
    message: validationErrorMessages.invalidEmail,
});

/**
 * Creates a pattern validation rule with custom error message
 * @param pattern - Regex pattern
 * @param errorMessage - Custom error message
 * @returns Validation rule object
 */
export const createPatternRule = (pattern: RegExp, errorMessage: string): Rule => ({
    pattern,
    message: errorMessage,
});

/**
 * Creates a custom validator rule
 * @param validator - Custom validation function
 * @param errorMessage - Error message for failed validation
 * @returns Validation rule object
 */
export const createCustomValidatorRule = (
    validator: (value: object) => boolean | Promise<boolean>,
    errorMessage: string,
): Rule => ({
    validator: async (_, value) => {
        const isValid = await validator(value);
        return isValid ? Promise.resolve() : Promise.reject(new Error(errorMessage));
    },
});

/**
 * Creates validation rules for a field with specific requirements
 * @param fieldName - The name of the field
 * @param options - Validation options
 * @returns Array of validation rules
 */
export const createFieldValidationRules = (
    fieldName: string,
    options: {
        required?: boolean;
        minLength?: number;
        maxLength?: number;
        pattern?: RegExp;
        patternErrorMessage?: string;
        customValidator?: (value: unknown) => boolean | Promise<boolean>;
        customErrorMessage?: string;
        email?: boolean;
    } = {},
): Rule[] => {
    const rules: Rule[] = [];

    // Required rule
    if (options.required !== false) {
        rules.push(createRequiredRule(fieldName));
    }

    // Min length rule
    if (options.minLength) {
        rules.push(createMinLengthRule(options.minLength));
    }

    // Max length rule
    if (options.maxLength) {
        rules.push(createMaxLengthRule(options.maxLength));
    }

    // Pattern rule
    if (options.pattern) {
        rules.push(
            createPatternRule(options.pattern, options.patternErrorMessage || 'Niepoprawny format'),
        );
    }

    // Custom validator rule
    if (options.customValidator) {
        rules.push(
            createCustomValidatorRule(
                options.customValidator,
                options.customErrorMessage || 'Niepoprawna wartość',
            ),
        );
    }

    // Email rule
    if (options.email) {
        rules.push(createEmailRule());
    }

    return rules;
};
